<script lang="ts" setup>
import { onClickOutside } from '@vueuse/core';

type DropdownOption = { title?: string; value: string | number; [key: string]: unknown };

const modelValue = defineModel<string | number | DropdownOption | null>();

const { options, optionLabel } = defineProps<{
  options: { title: string; value: string | number }[];
  optionLabel: string;
}>();
const emit = defineEmits(['change']);

const expanded = ref(false);
const dropdownList = ref();

const label = computed(() => {
  if (modelValue.value) {
    return optionLabel && typeof modelValue.value === 'object' ? modelValue.value[optionLabel] : modelValue.value;
  }
  return '';
});

onClickOutside(dropdownList, () => {
  hideDropdownList();
});

function selectOption(option: DropdownOption) {
  hideDropdownList();
  emit('change', option);
}

function hideDropdownList() {
  expanded.value = false;
}

function switchDropdownList() {
  expanded.value = !expanded.value;
}
</script>

<template>
  <div
    ref="dropdownList"
    class="dropdown"
  >
    <button
      class="dropdown__button"
      @click="switchDropdownList"
    >
      {{ label }}

      <SvgoChevronDown
        :class="{ 'rotate-180': expanded }"
        :font-controlled="false"
        class="size-5"
        filled
      />
    </button>

    <ul
      v-if="expanded"
      class="dropdown__list"
    >
      <li
        v-for="option in options"
        :key="option.title"
        :class="{
          'dropdown__list-item--selected':
            option.value === (typeof modelValue === 'object' ? modelValue?.value : modelValue),
        }"
        class="dropdown__list-item"
        @click="selectOption(option)"
      >
        {{ option.title }}
      </li>
    </ul>
  </div>
</template>

<style lang="scss" scoped>
.dropdown {
  position: relative;

  &__button {
    display: flex;
    gap: 4px;
    align-items: center;
    padding: 6px;
  }

  &__list {
    position: absolute;
    top: 34px;
    left: 0;
    z-index: 100;
    width: max-content;
    margin: 0;
    padding: 0;
    border: 1px solid $black-10;
    border-radius: 4px;
    background-color: $white;
    list-style: none;
  }

  &__list-item {
    padding: 8px 15px;
    border-radius: 4px;
    transition: all 0.2s;

    &:hover {
      background-color: $black-10;
      color: $black;
      cursor: pointer;
    }

    &--selected {
      background-color: $black;
      color: $white;

      &:hover {
        background-color: $black;
        color: $white;
      }
    }
  }
}
</style>
